import type { Landmark } from '../../../data/types';
import type { Ring, Vec2 } from '../../../geo/types';
import type { MeshBuilder } from '../../mesh/meshBuilder';
import { buildVesselRoof } from './churchVessel';
import type { VesselPlan } from './churchModel';
import { buttress, centredWindow, gableWall, pointedWindows, rectWall } from './masonry';
import { meshFrameOf, planBounds, planFrameOf } from './planFrame';
import { paintedChapelStyles, type PaintedChapelColors } from './paintedChapels';
import { NO_TRACERY, type TracerySink } from './tracery/tracerySink';

/** Baart's archaeological reconstruction establishes 32 × 9 × 13 m overall.
 * Eaves, openings and hidden east termination remain painted/typological estimates. */
export function onzeLieveVrouwePlan(landmark: Landmark) {
  const plan = planFrameOf(landmark.footprint, landmark.heading);
  const b = planBounds(plan);
  const vessel: VesselPlan = {
    u0: b.u0,
    chordU: b.u1,
    vc: (b.v0 + b.v1) / 2,
    half: (b.v1 - b.v0) / 2,
    eaveY: Number(landmark.params.naveHeight),
    ridgeY: Number(landmark.params.ridgeHeight),
    apse: 'flat',
  };
  return { frame: meshFrameOf(plan), plan, vessel };
}

export function buildOnzeLieveVrouwekapel(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: PaintedChapelColors,
  ids: { edgeId: number; seed: number },
  tracery: TracerySink = NO_TRACERY
): number {
  const before = builder.triangleCount;
  const styles = paintedChapelStyles(colors, ids, tracery);
  const { frame, vessel: v } = onzeLieveVrouwePlan(landmark);
  const lo = v.vc - v.half,
    hi = v.vc + v.half,
    width = v.half * 2;
  const heights = { eaveY: v.eaveY, apexY: v.ridgeY };
  // The glazing builder gives arbitrary openings recessed stone reveals; it assigns
  // pointed tracery only to pointed windows, leaving the circular oculus plain.
  const circle: Ring = Array.from({ length: 16 }, (_, i): Vec2 => {
    const angle = (i * Math.PI) / 8;
    return [width / 2 + 0.55 * Math.cos(angle), 10.3 + 0.55 * Math.sin(angle)];
  });
  gableWall(
    builder,
    styles,
    frame,
    [
      [v.u0, hi],
      [v.u0, lo],
    ],
    heights,
    [...centredWindow(width, 0, 3.4, 0.2), ...centredWindow(width, 4, 8, 0.3), circle]
  );
  gableWall(
    builder,
    styles,
    frame,
    [
      [v.chordU, lo],
      [v.chordU, hi],
    ],
    heights,
    centredWindow(width, 2, 6.6, 0.3)
  );
  for (const side of [-1, 1]) {
    const vc = v.vc + side * v.half;
    const a: Vec2 = side < 0 ? [v.u0, vc] : [v.chordU, vc];
    const b: Vec2 = side < 0 ? [v.chordU, vc] : [v.u0, vc];
    rectWall(
      builder,
      styles,
      frame,
      [a, b],
      [0, v.eaveY],
      pointedWindows(v.chordU - v.u0, 6.4, 1.8, 6.6)
    );
    for (let u = v.u0 + 6.4; u < v.chordU - 1; u += 6.4)
      buttress(builder, styles, frame, [u, vc], [0, side], {
        width: 0.55,
        depth: 0.22,
        height: 5.8,
        pinnacle: 0,
      });
  }
  buildVesselRoof(builder, styles, frame, v);
  return builder.triangleCount - before;
}
